import { useRef, useState } from "react";

import { usePhotoUpload } from "./usePhotoUpload";
import { useCart } from "../cart/CartContext";
import { uploadCartTempFiles } from "../api/CartTempFiles";
import { createCartItemId } from "../cart/cartUtils";
import {
  MAX_PHOTOS,
  VALID_PACK_QTYS,
} from "../constants/photoProduct";
import { getCurrentPack } from "../utils/getCurrentPack";

export function usePhotoUploadPage(photoPacks = []) {
  const { addItem } = useCart();
  const inputRef = useRef(null);

  const [toast, setToast] = useState("");

  const [cropQueue, setCropQueue] = useState([]);
  const [cropPhoto, setCropPhoto] = useState(null);

  const {
    photos,
    addPhoto,
    removePhoto,
    clearPhotos,
  } = usePhotoUpload(setToast);

  const startCropFlow = (fileList) => {
    const remaining = MAX_PHOTOS - photos.length;

    if (remaining <= 0) {
      setToast(`Ya has alcanzado el máximo de ${MAX_PHOTOS} fotos.`);
      return;
    }

    const imageFiles = [...fileList].filter((file) =>
      file.type?.startsWith("image/")
    );

    if (!imageFiles.length) {
      setToast("Selecciona archivos de imagen válidos.");
      return;
    }

    if (imageFiles.length > remaining) {
      setToast(
        `Has seleccionado demasiadas imágenes. Puedes añadir ${remaining} foto${remaining === 1 ? "" : "s"} más.`
      );
      return;
    }

    const queue = imageFiles.map((file) => ({
      file,
      name: file.name,
      preview: URL.createObjectURL(file),
    }));

    setCropQueue(queue.slice(1));
    setCropPhoto(queue[0]);
  };

  const handleInputChange = (e) => {
    startCropFlow(e.target.files);
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    startCropFlow(e.dataTransfer.files);
  };

  const openNextCrop = (queue) => {
    if (queue.length > 0) {
      setCropPhoto(queue[0]);
      setCropQueue(queue.slice(1));
    } else {
      setCropPhoto(null);
      setCropQueue([]);
    }
  };

  const closeCrop = () => {
    if (cropPhoto?.preview) {
      URL.revokeObjectURL(cropPhoto.preview);
    }

    openNextCrop(cropQueue);
  };

  const confirmCrop = (file) => {
    if (cropPhoto?.preview) {
      URL.revokeObjectURL(cropPhoto.preview);
    }

    addPhoto(file);
    openNextCrop(cropQueue);
  };

  const validatePackQuantity = () => {
    if (VALID_PACK_QTYS.includes(photos.length)) {
      return true;
    }

    const selectedQty = photos.length;

    const lowerPack = [...VALID_PACK_QTYS]
      .filter((qty) => qty < selectedQty)
      .sort((a, b) => b - a)[0];

    const higherPack = [...VALID_PACK_QTYS]
      .filter((qty) => qty > selectedQty)
      .sort((a, b) => a - b)[0];

    if (!lowerPack && higherPack) {
      setToast(
        `Necesitas ${higherPack - selectedQty} foto${higherPack - selectedQty === 1 ? "" : "s"} más para completar el pack mínimo de ${higherPack}.`
      );
      return false;
    }

    if (lowerPack && higherPack) {
      setToast(
        `No hay pack de ${selectedQty} fotos. Puedes quitar ${selectedQty - lowerPack} foto${selectedQty - lowerPack === 1 ? "" : "s"} para el pack de ${lowerPack} o añadir ${higherPack - selectedQty} foto${higherPack - selectedQty === 1 ? "" : "s"} para el pack de ${higherPack}.`
      );
      return false;
    }

    if (lowerPack && !higherPack) {
      setToast(
        `El máximo disponible es el pack de ${lowerPack} fotos. Para otras cantidades, consúltanos.`
      );
      return false;
    }

    setToast("Cantidad no disponible. Para otras cantidades, consúltanos.");
    return false;
  };

  const handleSubmit = async () => {
    if (!photos.length) {
      setToast("Selecciona al menos una foto");
      return;
    }

    if (!validatePackQuantity()) {
      return;
    }

    const currentPack = getCurrentPack(photos.length, photoPacks);

    if (!currentPack) {
      setToast("No se ha encontrado un pack para esta cantidad.");
      return;
    }

    const cartItemId = createCartItemId(currentPack.id);
    const files = photos.map((photo) => photo.file);
    let tempFiles = [];

    try {
      tempFiles = await uploadCartTempFiles({ cartItemId, files });
    } catch (error) {
      setToast("No se pudieron subir las imagenes. Intentalo de nuevo.");
      return;
    }

    addItem({
      cartItemId,
      productId: currentPack.id,
      productName: currentPack.name,
      category: currentPack.category,
      type: currentPack.type,
      quantity: 1,
      unitPrice: currentPack.price || 0,
      currency: currentPack.currency || "EUR",
      options: {
        photos: {
          label: "Fotos",
          value: String(photos.length),
          valueLabel: `${photos.length} foto${photos.length === 1 ? "" : "s"}`,
        },
      },
      assets: photos.map((photo, index) => ({
        kind: "uploaded_image",
        role: "photo_magnet",
        fileName: photo.name || `foto_${index + 1}.jpg`,
        includedInOrder: true,
      })),
      tempFiles,
      notes: [
        "Imagenes subidas temporalmente hasta crear el pedido.",
      ],
    });

    setToast("Pack de imanes añadido al carrito.");
  };

  const handleClear = () => {
    clearPhotos();

    if (cropPhoto?.preview) {
      URL.revokeObjectURL(cropPhoto.preview);
    }

    cropQueue.forEach((p) => {
      if (p.preview) URL.revokeObjectURL(p.preview);
    });

    setCropPhoto(null);
    setCropQueue([]);
  };

  return {
    toast,

    photos,

    cropPhoto,

    inputRef,

    handleInputChange,
    handleDrop,

    removePhoto,

    closeCrop,
    confirmCrop,

    handleClear,
    handleSubmit,
  };
}
